import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchCreator } from "@/lib/api";
import { useAuth, authClient } from "@/lib/auth";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";
import { Youtube, Twitch, Instagram, Twitter, Facebook, Globe, Github, Users, Heart, Sparkles, ExternalLink, Play } from "lucide-react";

const SOCIAL_META = {
  youtube: { icon: Youtube, color: "#FF0033" },
  twitch: { icon: Twitch, color: "#9146FF" },
  kick: { icon: Play, color: "#53FC18" },
  instagram: { icon: Instagram, color: "#E4405F" },
  twitter: { icon: Twitter, color: "#1DA1F2" },
  facebook: { icon: Facebook, color: "#1877F2" },
  tiktok: { icon: Play, color: "#FF0050" },
  discord: { icon: Users, color: "#5865F2" },
  website: { icon: Globe, color: "#FFBF00" },
  github: { icon: Github, color: "#ffffff" },
  other: { icon: ExternalLink, color: "#ffffff" },
};

export default function CreatorProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchCreator(id).then(setC).catch(() => setC(null)); }, [id]);

  const toggleFollow = async () => {
    if (!user) return toast.error("Sign in to follow creators");
    setBusy(true);
    try {
      if (c.is_following) {
        const { data } = await authClient.delete(`/creators/${id}/follow`);
        sfx.no();
        setC({ ...c, is_following: false, followers_count: data.followers });
      } else {
        const { data } = await authClient.post(`/creators/${id}/follow`);
        sfx.pling();
        setC({ ...c, is_following: true, followers_count: data.followers });
      }
    } catch (e) { sfx.error(); toast.error("Failed"); }
    setBusy(false);
  };

  if (!c) return <div className="pt-40 text-center text-white/50">Loading…</div>;

  return (
    <div className="pt-24 pb-24" data-testid="page-creator-profile">
      {/* Banner */}
      <div className="relative h-48 sm:h-72 overflow-hidden">
        {c.banner ? <img src={c.banner} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gradient-to-br from-[#FFBF00]/40 to-[#FF8C00]/20" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-16 relative">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <img src={c.avatar_url || c.avatar} alt="" className="w-28 h-28 rounded-2xl border-4 border-black object-cover" data-testid="creator-avatar" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Creator</span>
              {c.featured_creator && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FFBF00]/15 border border-[#FFBF00]/40 text-[#FFBF00] font-bold inline-flex items-center gap-1">
                  <Sparkles size={10} /> Featured
                </span>
              )}
              {c.creator_level > 0 && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/80 font-mono">
                  Lvl {c.creator_level}
                </span>
              )}
            </div>
            <div className="font-hero text-4xl sm:text-5xl mt-1">{c.name}</div>
            {c.mc_username && <div className="text-sm font-mono text-[#FFBF00]/80 mt-1">{c.mc_username}</div>}
            {c.bio && <p className="text-white/70 text-sm mt-2 max-w-2xl">{c.bio}</p>}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="font-hero text-2xl text-[#FFBF00]">{(c.followers_count || 0).toLocaleString("en-IN")}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Followers</div>
            </div>
            <div className="text-center">
              <div className="font-hero text-2xl">{(c.following_count || 0).toLocaleString("en-IN")}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Following</div>
            </div>
            {user && user.id !== c.id && (
              <button
                onClick={toggleFollow} disabled={busy}
                data-testid="creator-follow-btn"
                className={`px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-semibold transition-colors ${
                  c.is_following ? "border border-white/20 hover:border-red-400/50 text-white/80"
                                 : "bg-[#FFBF00] hover:bg-[#FF8C00] text-black"
                }`}
              >
                {c.is_following ? "Following" : "Follow"}
              </button>
            )}
          </div>
        </div>

        {/* Socials */}
        {c.social && Object.entries(c.social).filter(([, v]) => v).length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {Object.entries(c.social).filter(([, v]) => v).map(([k, v]) => {
              const meta = SOCIAL_META[k] || SOCIAL_META.other;
              const I = meta.icon;
              const rawValue = String(v || "").trim();
              let href;

              if (k.toLowerCase() === "youtube") {
                if (/^https?:\/\//i.test(rawValue)) {
                  href = rawValue;
                } else {
                  const handle = rawValue.replace(/^@/, "");
                  href = `https://www.youtube.com/@${handle}`;
                }
              } else {
                href = /^https?:\/\//i.test(rawValue)
                  ? rawValue
                  : `https://${rawValue}`;
              }
              return (
                <a key={k} href={href} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass hover:border-[#FFBF00]/40 text-xs"
                   style={{ borderColor: `${meta.color}33` }}
                   data-testid={`social-${k}`}>
                  <I size={13} style={{ color: meta.color }} />
                  <span className="text-white/80 capitalize">{k}</span>
                </a>
              );
            })}
          </div>
        )}

        {/* Minecraft */}
        {c.mc_username && (
          <div className="mt-8 rounded-2xl glass p-6 flex items-center gap-6" data-testid="creator-mc">
            <img src={`https://mc-heads.net/body/${c.mc_username}/left`} alt="" className="h-40 object-contain drop-shadow-[0_0_18px_rgba(255,191,0,0.25)]" onError={(e) => e.target.style.display = "none"} />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#FFBF00]">Minecraft Identity</div>
              <div className="font-hero text-3xl mt-1">{c.mc_username}</div>
              <div className="text-[11px] font-mono text-white/40 mt-1">UUID · {c.mc_uuid}</div>
              {c.favorite_server && <div className="text-xs text-white/60 mt-2">Favourite server: <span className="text-[#FFBF00]">{c.favorite_server}</span></div>}
            </div>
          </div>
        )}

        {/* Latest builds */}
        {c.latest_builds?.length > 0 && (
          <section className="mt-10">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Latest Builds</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {c.latest_builds.map((b) => (
                <Link key={b.id} to="/builds" className="relative rounded-lg overflow-hidden group border border-white/10">
                  {(b.images?.[0] || b.image) && (
                    <img src={b.images?.[0] || b.image} alt="" className="w-full h-32 object-cover" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-2 left-2 text-xs font-semibold">{b.title}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Videos — powered by iter16 tier-aware sync */}
        <CreatorVideosSection creatorId={c.id} tier={c.creator_tier} tierMeta={c.creator_tier_meta} />

        <div className="mt-10 text-center">
          <Link to="/creators" onClick={() => sfx.click()} className="text-xs uppercase tracking-widest text-white/60 hover:text-[#FFBF00]">← Back to Creator Hub</Link>
        </div>
      </div>
    </div>
  );
}

function CreatorVideosSection({ creatorId, tier, tierMeta }) {
  const [tab, setTab] = React.useState("featured");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/creator-sync/creator/${creatorId}/videos?kind=${tab}`)
      .then(r => r.json())
      .then(d => { if (alive) setItems(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [creatorId, tab]);

  const tabs = [
    { key: "featured", label: "Featured" },
    { key: "video", label: "Videos" },
    { key: "short", label: "Shorts" },
    { key: "live", label: "Live" },
  ];

  return (
    <section className="mt-10" data-testid="creator-videos">
      <div className="flex items-center flex-wrap gap-2 mb-3">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFBF00]">Latest Videos</div>
        {tier && tierMeta && (
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold"
                style={{ backgroundColor: tierMeta.color, color: "#000" }}
                data-testid={`creator-tier-badge-${tier}`}>
            {tierMeta.label}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`creator-videos-tab-${t.key}`}
                    className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest transition ${
                      tab === t.key ? "bg-[#FFBF00] text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"
                    }`}>{t.label}</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="text-center text-white/40 py-6 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl glass p-8 text-center text-white/50 text-sm" data-testid="creator-videos-empty">
          Nothing yet in <b className="text-white/70">{tab}</b>.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((v) => (
            <a key={v.id} href={`https://www.youtube.com/watch?v=${v.video_id}`} target="_blank" rel="noreferrer"
               data-testid={`creator-video-${v.id}`}
               className="rounded-xl glass overflow-hidden hover:border-[#FFBF00]/40 block group">
              <div className={`relative ${v.is_short ? "aspect-[9/16]" : "aspect-video"}`}>
                {v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />}
                {v.is_live && <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-red-500 text-white text-[10px] uppercase font-bold animate-pulse" data-testid={`video-live-${v.id}`}>● LIVE</div>}
                {v.is_upcoming && <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-yellow-500 text-black text-[10px] uppercase font-bold" data-testid={`video-upcoming-${v.id}`}>UPCOMING</div>}
                {v.is_short && !v.is_live && <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-white/90 text-black text-[10px] uppercase font-bold" data-testid={`video-short-${v.id}`}>SHORT</div>}
                <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-mono">
                  {Math.floor((v.duration_seconds || 0) / 60)}:{String((v.duration_seconds || 0) % 60).padStart(2, "0")}
                </div>
              </div>
              <div className="p-3">
                <div className="text-sm font-semibold line-clamp-2">{v.title}</div>
                <div className="mt-1 text-[10px] text-white/40 uppercase tracking-widest flex items-center gap-2">
                  <span>{(v.view_count || 0).toLocaleString("en-US")} views</span>
                  {v.hashtags_ok && <span className="text-emerald-400">· ✓ LostDC tags</span>}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
